// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { Editor as DraftEditor } from 'react-draft-wysiwyg';
import { EditorState } from 'draft-js';
import { Editor } from './Editor';
import { TextEditorFooter } from './TextEditorFooter';
import {
  StyledEditorSide,
  StyledEditorWrapper,
  StyledScreenWrapper,
  StyledTextEditor,
  StyledTextEditorContent,
  StyledTextEditorContentInner,
} from '../../Styled';
import { EmergentContentSection, EmergentExemplar } from '../../../types';
import { getEmergentSections } from '../../../utils';
import { getExemplarLinesLimits, getTextEditorProps } from '../../../utils/textEditor/common';
import {
  getCurrentPageEmergentContent,
  getEmergentEditorStateFromSide,
  getEmergentTextEditorToolbar,
  getNewEmergentFromSides,
} from '../../../utils/textEditor/emergentTextEditor';
import { WORDS_AND_SENTENCES } from '../../../constants/navigation';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

interface IEmergentTextEditor {
  exemplar: EmergentExemplar;
  defaultExemplar: EmergentExemplar;
  mainColor: string;
  page: string;
  updateEditedExemplar: (editedExemplar: EmergentExemplar) => void;
  onSave: (exemplar: EmergentExemplar) => void;
}

export const EmergentTextEditor: React.FC<IEmergentTextEditor> = (props): JSX.Element => {
  const { exemplar, defaultExemplar, page, mainColor, updateEditedExemplar, onSave } = props;

  const partOfExemplar = useMemo(() => getCurrentPageEmergentContent(exemplar.content, page), [exemplar.content]);
  const partOfDefaultExemplar = useMemo(
    () => getCurrentPageEmergentContent(defaultExemplar.content, page),
    [defaultExemplar.content]
  );
  const initLeftEditorState = useMemo(
    () => getEmergentEditorStateFromSide(partOfExemplar.content.left),
    [exemplar.content]
  );
  const initRightEditorState = useMemo(
    () => getEmergentEditorStateFromSide(partOfExemplar.content.right),
    [exemplar.content]
  );
  const exemplarLinesLimits = useMemo(
    () => getExemplarLinesLimits(partOfDefaultExemplar.content),
    [partOfDefaultExemplar]
  );

  const [leftEditorState, setLeftEditorState] = useState(initLeftEditorState);
  const [rightEditorState, setRightEditorState] = useState(initRightEditorState);
  const [isFocusedEditor, setIsFocusedEditor] = useState(false);

  const updateLeftSide = (id: string | number) => {
    return (state: EditorState) => {
      setLeftEditorState({
        ...leftEditorState,
        [id]: {
          value: state,
          meta: leftEditorState[id].meta,
        },
      });
    };
  };

  const updateRightSide = (id: string | number) => {
    return (state: EditorState) => {
      setRightEditorState({
        ...rightEditorState,
        [id]: {
          value: state,
          meta: rightEditorState[id].meta,
        },
      });
    };
  };

  const handleFocus = () => {
    setIsFocusedEditor(true);
  };

  const handleBlur = () => {
    const updatedExemplar = getNewEmergentFromSides(exemplar, leftEditorState, rightEditorState, partOfExemplar);
    updateEditedExemplar(updatedExemplar);
    setIsFocusedEditor(false);
  };

  const save = () => {
    onSave(getNewEmergentFromSides(exemplar, leftEditorState, rightEditorState, partOfExemplar));
  };

  const showColorPicker = page === WORDS_AND_SENTENCES;
  const renderTextEditor = () => (
    <StyledEditorWrapper>
      <StyledEditorSide>
        {getEmergentSections(partOfExemplar.content.left).map((section: EmergentContentSection, index) => {
          const isTitle = section.style?.includes('heading');

          return (
            <Editor
              key={section.id}
              id={section.id}
              toolbarConfig={getEmergentTextEditorToolbar(showColorPicker)}
              editorState={(leftEditorState[section.id] || {}).value}
              blocksLimit={exemplarLinesLimits[`left-${index}`]}
              isLimited={true}
              isTitle={isTitle}
              onEditorStateChange={updateLeftSide(section.id)}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          );
        })}
      </StyledEditorSide>
      <StyledEditorSide>
        <StyledTextEditor data-testid="emergent-editor" isHidden={true}>
          <DraftEditor
            {...getTextEditorProps(false, true)}
            toolbarHidden={isFocusedEditor}
            toolbar={getEmergentTextEditorToolbar(showColorPicker)}
            key={`${partOfExemplar.content.left[0].id}-hidden`}
            defaultEditorState={(leftEditorState[partOfExemplar.content.left[0].id] || {}).value}
          />
        </StyledTextEditor>
        {getEmergentSections(partOfExemplar.content.right).map((section: EmergentContentSection, index) => {
          return (
            <Editor
              key={section.id}
              id={section.id}
              toolbarConfig={getEmergentTextEditorToolbar(showColorPicker)}
              editorState={(rightEditorState[section.id] || {}).value}
              blocksLimit={exemplarLinesLimits[`right-${index}`]}
              isLimited={true}
              isRight={true}
              onEditorStateChange={updateRightSide(section.id)}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          );
        })}
      </StyledEditorSide>
    </StyledEditorWrapper>
  );

  return (
    <StyledTextEditorContent>
      <StyledTextEditorContentInner>
        <StyledScreenWrapper>{renderTextEditor()}</StyledScreenWrapper>
        <TextEditorFooter mainColor={mainColor} onSave={save} />
      </StyledTextEditorContentInner>
    </StyledTextEditorContent>
  );
};
